import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { HeroBannerSlider } from "@/components/HeroBannerSlider";
import { useBrandName } from "@/context/BrandingContext";
import { useStorefrontConfig } from "@/context/StorefrontConfigContext";
import { useCompanyProfile } from "@/context/CompanyProfileContext";
import { faqJsonLd } from "@/lib/companyProfile";
import { CompanyHero } from "@/components/company/CompanyHero";
import { AboutSection, WhyUsSection, StatsSection, BusinessCtaSection } from "@/components/company/CompanySections";
import { GallerySection, FaqSection, LocationSection } from "@/components/company/CompanyGalleryFaq";
import { StorePreviewSection } from "@/components/company/CompanyStorePreview";

const RENDERERS = {
  about: AboutSection, why_us: WhyUsSection, store_preview: StorePreviewSection, business_cta: BusinessCtaSection,
  gallery: GallerySection, stats: StatsSection, faq: FaqSection, location: LocationSection,
};

/* Store Banner existing (Phase 3) — SELALU section visual pertama saat CP ON. Data & komponen sama dengan Home.jsx
   (GET /banners + HeroBannerSlider, layout/variant dari template). Bukan bagian section_order, tanpa toggle CP.
   Tidak ada banner aktif → tidak dirender (Hero CP tetap ada, tanpa layout kosong). */
function StoreBannerTop() {
  const { config } = useStorefrontConfig();
  const [banners, setBanners] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/banners").then((r) => alive && setBanners(r.data || [])).catch(() => {});
    return () => { alive = false; };
  }, []);
  const slides = banners.filter((b) => b.is_active !== false && b.enabled !== false);
  if (slides.length === 0) return null;
  const isPremium = config.design.homepage_layout === "premium-commerce";
  const slider = <HeroBannerSlider banners={banners} layout={isPremium ? "premium-commerce" : config.design.homepage_layout} variant={isPremium ? "image-focus" : "default"} />;
  return (
    <div className="cp-store-banner" data-testid="company-store-banner">
      {isPremium ? slider : <div className="storefront-container py-4 sm:py-6 lg:py-8">{slider}</div>}
    </div>
  );
}

/* Company Profile homepage (Phase 2/3). Urutan section = section_order tervalidasi backend; section yang
   tidak enabled/kosong tidak ada di `sections` → tidak dirender. Commerce tetap di /katalog (engine sama). */
export default function CompanyHomePage() {
  const brandName = useBrandName();
  const { hero, sections, sectionOrder, catalogPath } = useCompanyProfile();
  const { hash, pathname } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, sectionOrder]);

  // FAQPage JSON-LD hanya pada `/` (bukan catch-all soft-404 yang membawa noindex+canonical dari route).
  const faqLd = pathname === "/" && sections.faq ? faqJsonLd(sections.faq.items) : null;

  return (
    <div className="cp-home" data-testid="company-home">
      {faqLd ? <SEOHead jsonLd={[faqLd]} /> : null}
      <StoreBannerTop />
      <CompanyHero hero={hero} brandName={brandName} catalogPath={catalogPath} />
      <div className="storefront-container pb-10">
        {sectionOrder.map((key) => {
          const Section = RENDERERS[key];
          return Section && sections[key] ? <Section key={key} data={sections[key]} catalogPath={catalogPath} /> : null;
        })}
      </div>
    </div>
  );
}
